import { useCallback, useMemo, useRef } from "react";

// #668 / #671 — shared keyboard navigation for the score sheet, via Tab AND
// Enter, in whichever per-row order the scribe has chosen.
//
// `rowOrder` is the single knob that decides, per row, whether the mark or the
// comment field leads the sequence:
//   MARK_FIRST (default) — score → comment → next score → …
//   COMMENT_FIRST        — comment → score → next comment → …
// #671 threads it in from a persisted footer switch (store/sheetFieldOrder.js);
// scribes genuinely disagree about which order they want, so it's a per-scribe
// preference, not a fixed rule.
//
// Both keys are driven from the same `order` array, so they can never disagree:
//   - Enter doesn't move focus natively at all, so this hook gives it the
//     sequence outright, plus Shift+Enter as Shift+Tab's mirror.
//   - Tab DOES move focus natively, but only in DOM order — which is always
//     mark → comment. That's fine under MARK_FIRST and wrong under
//     COMMENT_FIRST, so Tab is driven from `order` too (see handleKeyNav).
//
// Safety: Enter is always swallowed and refuses to move past the last field, so
// Enter in General Remarks can never walk onto "Complete and sign" (#670 is the
// regression test for that). Tab deliberately still escapes the sheet at its
// ends — it only moves focus, it never fires a button.
export const MARK_FIRST = "mark-first";
export const COMMENT_FIRST = "comment-first";
export const GENERAL_REMARKS_KEY = "general-remarks";

export const markFieldKey = (rowId) => `mark:${rowId}`;
export const commentFieldKey = (rowId) => `comment:${rowId}`;

// #721 — after Tab/Enter moves focus, keep the newly-focused field visible.
// The sheet has a sticky sub-header (top) and a sticky footer holding the
// running score (#725, bottom); focus can land on a field tucked behind either
// where the scribe can't see what they're typing, or entirely below the fold.
// We measure both sticky bars live (no hardcoded offsets) and only scroll when
// the field is actually obscured or off-screen — a field already comfortably in
// view is left alone, so there's no jarring re-centering on every keystroke.
function keepFieldInView(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  if (typeof document === "undefined") return;

  const rect = el.getBoundingClientRect();

  const subHeader = document.querySelector('[data-testid="scoring-subheader"]');
  const footer = document.querySelector('[data-testid="scoring-footer"]');

  // Bottom edge of the sticky header stack (SessionHeader + sub-header row).
  const safeTop = subHeader ? subHeader.getBoundingClientRect().bottom : 0;
  // Top edge of the sticky footer (or the viewport bottom if it isn't there).
  const viewportBottom =
    window.innerHeight || document.documentElement.clientHeight;
  const safeBottom = footer
    ? footer.getBoundingClientRect().top
    : viewportBottom;

  const obscuredAbove = rect.top < safeTop;
  const obscuredBelow = rect.bottom > safeBottom;
  if (obscuredAbove || obscuredBelow) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}

// #1449 — when a sheet's def sets commentMode "generalOnly", ScoringForm
// renders no per-movement/per-collective remarks input at all, so a key for
// one must never enter `order`: if it did, targetOf would resolve it to a
// key with no registered ref (registerField is never called for a field
// that isn't rendered), moveFocus/handleKeyNav would see an undefined
// target and no-op, and Tab/Enter from the mark field would silently die
// instead of advancing to the next row — a dead tab stop. Dropping the
// comment key per row up front keeps `order` an accurate map of what's
// actually on screen, so navigation never stalls.
function rowKeys(rowId, rowOrder, commentsEnabled) {
  const mark = markFieldKey(rowId);
  if (!commentsEnabled) return [mark];
  const comment = commentFieldKey(rowId);
  return rowOrder === COMMENT_FIRST ? [comment, mark] : [mark, comment];
}

/**
 * @param {string[]} rowIds - unique row identifiers, in the order they
 *   render (movement rows, then collective rows).
 * @param {string} rowOrder - MARK_FIRST (default) or COMMENT_FIRST.
 * @param {boolean} commentsEnabled - false when the sheet's def sets
 *   commentMode "generalOnly" (#1449): per-row comment fields aren't
 *   rendered, so their nav keys are excluded — only the mark field and,
 *   at the end, GENERAL_REMARKS_KEY remain in the sequence.
 */
export function useSheetFieldNav({
  rowIds,
  rowOrder = MARK_FIRST,
  commentsEnabled = true,
}) {
  const fieldRefs = useRef(new Map());

  const order = useMemo(
    () => [
      ...rowIds.flatMap((id) => rowKeys(id, rowOrder, commentsEnabled)),
      GENERAL_REMARKS_KEY,
    ],
    [rowIds, rowOrder, commentsEnabled],
  );

  // Ref callback factory — hands each field's native input node to the map
  // keyed by its sequence key, so Enter navigation can call .focus() on it
  // directly (same target Tab would reach).
  const registerField = useCallback(
    (key) => (el) => {
      if (el) fieldRefs.current.set(key, el);
      else fieldRefs.current.delete(key);
    },
    [],
  );

  // The element the sequence says comes next (or previous) — or undefined at
  // either end of the sheet.
  const targetOf = useCallback(
    (fromKey, direction) => {
      const idx = order.indexOf(fromKey);
      if (idx === -1) return undefined;
      const targetKey = order[idx + direction];
      if (!targetKey) return undefined;
      return fieldRefs.current.get(targetKey);
    },
    [order],
  );

  const moveFocus = useCallback(
    (fromKey, direction) => {
      // Off either end is a no-op — most importantly, forward off the last
      // field (General Remarks) must never focus whatever follows it in
      // the DOM (the "Complete and sign" button).
      const target = targetOf(fromKey, direction);
      if (!target) return;
      target.focus();
      keepFieldInView(target); // #721
    },
    [targetOf],
  );

  // #671 — Tab and Enter must BOTH walk the scribe's chosen order.
  //
  // Enter never moved focus natively, so #668 drives it entirely from `order`.
  // Tab is the opposite: it moves focus natively, in DOM order, which is always
  // mark → comment. That's why MARK_FIRST needed no Tab handling. But under
  // COMMENT_FIRST the DOM no longer matches the sequence, so Tab has to be
  // driven from `order` too — otherwise Tab and Enter would walk a row in
  // opposite orders. Intercepting Tab in MARK_FIRST is a no-op in effect: it
  // reproduces exactly the sequence the browser would have walked anyway (the
  // only tabbables inside the sheet are the score/comment fields — the gavel,
  // flag and annotation word tokens are all tabindex -1 per #535/#668).
  //
  // The one asymmetry, and it is deliberate: at the ends of the sheet Tab
  // FALLS THROUGH to the browser instead of being swallowed. Tab must still be
  // able to leave the sheet (forward onto "Complete and sign", backward out of
  // it) or the sheet becomes a keyboard trap. That is safe because Tab only
  // MOVES focus — it never activates a button. Enter is the dangerous one, so
  // Enter stays a hard no-op at both ends: it is always preventDefault'd and
  // can never walk onto, let alone fire, "Complete and sign" (#670).
  const handleKeyNav = useCallback(
    (key) => (e) => {
      if (e.key === "Enter") {
        // Always preventDefault on Enter so a multiline comment box never gets
        // a literal newline (#669 hardens the field itself; this is the base
        // guard for every field in the sequence, including score inputs), and
        // so Enter can never submit anything.
        e.preventDefault();
        moveFocus(key, e.shiftKey ? -1 : 1);
        return;
      }

      if (e.key === "Tab") {
        const target = targetOf(key, e.shiftKey ? -1 : 1);
        // At either end: let the browser tab out of the sheet normally.
        if (!target) return;
        e.preventDefault();
        target.focus();
        keepFieldInView(target); // #721
      }
    },
    [moveFocus, targetOf],
  );

  return { registerField, handleKeyNav };
}
